import {
  Badge,
  BodyText,
  Button,
  Callout,
  Tooltip,
  toast,
} from "@/components/ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { EpisodeOfCare, getCodeValue } from "@curai/js-fhir-tools";
import { useParams, useSearch } from "@tanstack/react-router";
import { CheckCheck, CirclePlus } from "lucide-react";
import { useState } from "react";
import { SectionContent } from "../components/SectionContent";
import { SectionHeader } from "../components/SectionHeader";
import { AVCallSuggestedConditions } from "./components/AVCallSuggestedConditions/AVCallSuggestedConditions";
import { AddMultipleMedicalHistoryItems } from "./components/AddMultipleMedicalHistoryItems";
// TODO(viggyfresh): move VERIFICATION_STATUS_SYSTEM_URL to constants
import {
  MedicalHistoryItem,
  VERIFICATION_STATUS_SYSTEM_URL,
} from "./components/MedicalHistoryItem";
import { MedicalHistorySkeleton } from "./components/MedicalHistorySkeleton";
import {
  getUnActionedAVCallHistoricalCount,
  useAVCallSuggestedConditions,
  useBatchAddMedicalHistory,
  useConfirmMultipleConditions,
  useConfirmMultipleMedicalHistory,
  useGetMedicalHistory,
  useGetProblemList,
} from "./network";
import type { MedicalHistory as MedicalHistoryRecord } from "./types";

export const MedicalHistory = () => {
  const { patientId } = useParams({ strict: false });
  const { encounterId: encounterIdRaw } = useSearch({
    from: "/patient/$patientId",
  });
  // Route schema declares encounterId as z.union([z.number(), z.string()]);
  // normalize once so downstream string equality / template literals are safe.
  const encounterId = encounterIdRaw?.toString();
  const [isAddingMedicalHistory, setIsAddingMedicalHistory] = useState(false);

  const { data: isAiFeedEnabled } = useConfigCatFeatureFlag(
    "ehr-ai-feed-enabled",
  );
  const { data: isSingleEncounterVisitsEnabled } = useConfigCatFeatureFlag(
    "single-encounter-visits",
  );

  const { data: avCallSuggestions } = useAVCallSuggestedConditions({
    patientId: patientId as string,
    encounterId,
  });
  const unActionedAVCallCount =
    getUnActionedAVCallHistoricalCount(avCallSuggestions);

  const { mutate: batchAddMedicalHistory, isPending: isBatchAdding } =
    useBatchAddMedicalHistory({
      patientId: patientId as string,
      onSuccess: ({ addedCount, failedCount }) => {
        if (addedCount > 0) {
          setIsAddingMedicalHistory(false);
        }

        if (failedCount > 0) {
          toast.error(`${failedCount} medical history item(s) failed to add`);
        }
      },
      onError: (error) => {
        toast.error(
          `Failed to add medical history: ${error.response?.data?.detail || error.message}`,
        );
      },
    });

  const { data, isLoading, isError, refetch } = useGetMedicalHistory({
    patientId: patientId as string,
    enabled: isSingleEncounterVisitsEnabled !== true,
  });
  const {
    data: problemListData,
    isLoading: isProblemListLoading,
    isError: isProblemListError,
    refetch: refetchProblemList,
  } = useGetProblemList({
    patientId: patientId as string,
    enabled: isSingleEncounterVisitsEnabled === true,
  });
  const singleEncounterMedicalHistory = problemListData?.results.map(
    ({ condition, needsConfirmation, availableActions }) => {
      const isResolved = condition.clinicalStatusCode === "resolved";

      return {
        availableActions:
          availableActions as MedicalHistoryRecord["availableActions"],
        condition,
        episodeOfCare: new EpisodeOfCare({
          resourceType: "EpisodeOfCare",
          id: condition.id,
          status: isResolved ? "finished" : "active",
        }),
        lastVisit: null,
        needsConfirmation,
      };
    },
  );
  const medicalHistoryData =
    isSingleEncounterVisitsEnabled === true
      ? singleEncounterMedicalHistory
      : data?.results;
  const medicalHistoryIsLoading =
    isSingleEncounterVisitsEnabled === true ? isProblemListLoading : isLoading;
  const medicalHistoryIsError =
    isSingleEncounterVisitsEnabled === true ? isProblemListError : isError;
  const refetchMedicalHistory =
    isSingleEncounterVisitsEnabled === true ? refetchProblemList : refetch;

  const activeMedicalHistory = medicalHistoryData?.filter(
    (medicalHistory) => medicalHistory.episodeOfCare.status === "active",
  );

  const previousMedicalHistory = medicalHistoryData?.filter(
    (medicalHistory) => medicalHistory.episodeOfCare.status === "finished",
  );

  const unconfirmedMedicalHistory = medicalHistoryData?.filter(
    (medicalHistory) => {
      if (isSingleEncounterVisitsEnabled === true) {
        return medicalHistory.needsConfirmation === true;
      }

      return (
        medicalHistory.condition.verificationStatus &&
        getCodeValue(
          medicalHistory.condition.verificationStatus,
          VERIFICATION_STATUS_SYSTEM_URL,
        ) !== "confirmed"
      );
    },
  );

  const { mutate: confirmMultipleMedicalHistory, isPending: isConfirmingAll } =
    useConfirmMultipleMedicalHistory({
      patientId: patientId as string,
      episodeOfCareIds:
        unconfirmedMedicalHistory?.map(
          (medicalHistory) => medicalHistory.episodeOfCare.id as string,
        ) || [],
      onError: (error) => {
        toast.error(
          `Failed to confirm all medical history: ${error.response?.data?.detail || error.message}`,
        );
      },
    });

  const {
    mutate: confirmMultipleConditions,
    isPending: isConfirmingConditions,
  } = useConfirmMultipleConditions({
    patientId: patientId as string,
    onError: (error) => {
      toast.error(
        `Failed to confirm all medical history: ${error.response?.data?.detail || error.message}`,
      );
    },
  });

  const showSkeleton = medicalHistoryIsLoading;
  const showError =
    !showSkeleton && (medicalHistoryIsError || !medicalHistoryData);
  const showEmptyActive =
    !showSkeleton &&
    !showError &&
    medicalHistoryData &&
    activeMedicalHistory?.length === 0;
  const showEmptyPrevious =
    !showSkeleton &&
    !showError &&
    medicalHistoryData &&
    previousMedicalHistory?.length === 0;
  const showMedicalHistory =
    !showSkeleton &&
    !showError &&
    medicalHistoryData &&
    medicalHistoryData.length > 0;

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader
        title="Medical History"
        showSeparator={false}
        titleBadge={
          !isAiFeedEnabled && unActionedAVCallCount > 0 ? (
            <Badge variant="ai">
              <BodyText size="xs">{unActionedAVCallCount} suggested</BodyText>
            </Badge>
          ) : undefined
        }
        actionButtons={[
          ...(unconfirmedMedicalHistory?.length
            ? [
                <Tooltip
                  tooltipContentText="Confirm all"
                  side="bottom"
                  key="confirm-all"
                >
                  <Button
                    variant="icon"
                    className="m-0 p-0 text-link"
                    isLoading={isConfirmingAll || isConfirmingConditions}
                    data-testid="confirm-all-button"
                    onClick={() => {
                      trackEvent(TRACKERS.CONFIRM_ALL_MEDICAL_HISTORY_ITEMS);
                      if (isSingleEncounterVisitsEnabled) {
                        confirmMultipleConditions({
                          conditionIds: unconfirmedMedicalHistory.map(
                            (medicalHistory) =>
                              medicalHistory.condition.id as string,
                          ),
                        });

                        return;
                      }

                      confirmMultipleMedicalHistory({});
                    }}
                  >
                    <CheckCheck className="h-4 w-4" />
                  </Button>
                </Tooltip>,
              ]
            : []),
          <Tooltip tooltipContentText="Add" side="bottom" key="add">
            <Button
              variant="icon"
              className="m-0 p-0"
              onClick={() => {
                setIsAddingMedicalHistory(true);
              }}
              aria-label="Add medical history"
            >
              <CirclePlus className="stroke-link h-4 w-4" />
            </Button>
          </Tooltip>,
        ]}
      />
      <SectionContent>
        {isAddingMedicalHistory && (
          <div className="border border-primary p-3 rounded-md">
            <AddMultipleMedicalHistoryItems
              patientId={patientId as string}
              onCancel={() => {
                setIsAddingMedicalHistory(false);
              }}
              onSubmit={(conditionCodes) => {
                batchAddMedicalHistory({
                  patientId: patientId as string,
                  conditionCodes,
                });
              }}
              isSubmitting={isBatchAdding}
            />
          </div>
        )}
        <Tabs defaultValue="active">
          <TabsList className="mb-4">
            <TabsTrigger value="active" className="gap-1.5">
              Active
            </TabsTrigger>
            <TabsTrigger value="previous">Previous</TabsTrigger>
          </TabsList>
          <TabsContent value="active">
            {!isAiFeedEnabled && (
              <AVCallSuggestedConditions
                patientId={patientId as string}
                encounterId={encounterId}
              />
            )}
            {showSkeleton && <MedicalHistorySkeleton />}
            {showError && (
              <div className="flex flex-col w-full">
                <Callout
                  className="w-full cursor-pointer"
                  variant="danger"
                  title="Unable to load medical history!"
                  description="Please click here to try again."
                  onClick={() => refetchMedicalHistory()}
                />
              </div>
            )}
            {showEmptyActive && (
              <div className="flex w-full">
                <BodyText size="sm" textColor="tertiary">
                  No active medical history
                </BodyText>
              </div>
            )}
            {showMedicalHistory && (
              <div className="flex flex-col gap-2">
                {activeMedicalHistory?.map((medicalHistory) => (
                  <MedicalHistoryItem
                    key={medicalHistory.episodeOfCare.id}
                    medicalHistory={medicalHistory}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="previous">
            {showSkeleton && <MedicalHistorySkeleton />}
            {showError && (
              <div className="flex flex-col w-full">
                <Callout
                  className="w-full cursor-pointer"
                  variant="danger"
                  title="Unable to load medical history!"
                  description="Please click here to try again."
                  onClick={() => refetchMedicalHistory()}
                />
              </div>
            )}
            {showEmptyPrevious && (
              <div className="flex w-full">
                <BodyText size="sm" textColor="tertiary">
                  No previous medical history
                </BodyText>
              </div>
            )}
            {showMedicalHistory && (
              <div className="flex flex-col gap-2">
                {previousMedicalHistory?.map((medicalHistory) => (
                  <MedicalHistoryItem
                    key={medicalHistory.episodeOfCare.id}
                    medicalHistory={medicalHistory}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </SectionContent>
    </div>
  );
};
